import { useMemo, useState } from "react";
import { Mail, MapPin, Menu, MessageSquareMore, PhoneCall, X } from "lucide-react";
import { Link, NavLink } from "react-router-dom";

import { contactDetails } from "@/data/siteContent";
import { Button } from "@/components/ui/button";

export function SiteLayout({ content, language, onLanguageChange, children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navItems = Array.isArray(content?.nav) ? content.nav : [];

  const year = useMemo(() => new Date().getFullYear(), []);
  const quickLinksLabel = language === "hi" ? "क्विक लिंक्स" : "Quick links";
  const contactLabel = language === "hi" ? "संपर्क" : "Contact";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-primary/10 bg-primary text-primary-foreground">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 text-sm sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <p className="font-medium text-primary-foreground/95" data-testid="top-bar-message">
            {content.topBar}
          </p>
          <div className="flex flex-wrap gap-3 text-xs sm:text-sm">
            <a className="transition-opacity hover:opacity-80" href={contactDetails.phoneLink} data-testid="top-bar-call-link">
              {content.ctas.call}
            </a>
            <a className="transition-opacity hover:opacity-80" href={contactDetails.whatsappLink} target="_blank" rel="noreferrer" data-testid="top-bar-whatsapp-link">
              {language === "hi" ? "व्हाट्सऐप" : "WhatsApp"}
            </a>
            <a className="transition-opacity hover:opacity-80" href="/contact#quote-request" data-testid="top-bar-quote-link">
              {content.ctas.requestQuote}
            </a>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-50 border-b border-stone-200/80 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Link className="flex items-center gap-3" to="/" data-testid="site-logo-link">
            <img
              src={contactDetails.logoPath}
              alt="Sharda Agrotech logo"
              className="h-14 w-14 rounded-2xl border border-primary/10 object-cover"
              data-testid="site-logo-image"
            />
            <div>
              <p className="font-heading text-lg font-bold text-stone-900" data-testid="site-brand-name">
                {contactDetails.brandName}
              </p>
              <p className="text-xs font-medium uppercase tracking-[0.24em] text-accent" data-testid="site-brand-tagline">
                {contactDetails.tagline}
              </p>
            </div>
          </Link>

          <nav className="hidden items-center gap-6 lg:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.href}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors hover:text-primary ${isActive ? "text-primary" : "text-stone-700"}`
                }
                to={item.href}
                data-testid={`desktop-nav-${item.href.replace(/\//g, "") || "home"}-link`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <div className="flex rounded-full border border-stone-200 bg-stone-50 p-1" data-testid="language-toggle-group">
              <button
                type="button"
                onClick={() => onLanguageChange("en")}
                className={`rounded-full px-3 py-2 text-xs font-semibold transition-all ${language === "en" ? "bg-white text-primary shadow-sm" : "text-stone-500"}`}
                data-testid="language-toggle-en"
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => onLanguageChange("hi")}
                className={`rounded-full px-3 py-2 text-xs font-semibold transition-all ${language === "hi" ? "bg-white text-primary shadow-sm" : "text-stone-500"}`}
                data-testid="language-toggle-hi"
              >
                हिंदी
              </button>
            </div>

            <Button asChild className="rounded-full bg-accent px-5 text-accent-foreground transition-all duration-300 ease-in-out hover:-translate-y-0.5 hover:bg-accent/90" data-testid="header-request-quote-button">
              <a href="/contact#quote-request">{content.ctas.requestQuote}</a>
            </Button>

            <Button asChild variant="outline" className="rounded-full border-primary/20" data-testid="header-whatsapp-button">
              <a href={contactDetails.whatsappLink} target="_blank" rel="noreferrer">
                {language === "hi" ? "व्हाट्सऐप" : "WhatsApp"}
              </a>
            </Button>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((current) => !current)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 text-stone-700 lg:hidden"
            data-testid="mobile-menu-toggle-button"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {menuOpen ? (
          <div className="border-t border-stone-200 bg-white px-4 py-5 lg:hidden" data-testid="mobile-menu-panel">
            <div className="flex flex-col gap-4">
              {navItems.map((item) => (
                <NavLink
                  key={item.href}
                  className="text-sm font-medium text-stone-700"
                  to={item.href}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-nav-${item.href.replace(/\//g, "") || "home"}-link`}
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="flex gap-2">
                <button type="button" className={`rounded-full px-3 py-2 text-xs font-semibold ${language === "en" ? "bg-primary text-white" : "bg-stone-100 text-stone-600"}`} onClick={() => onLanguageChange("en")} data-testid="mobile-language-toggle-en">
                  EN
                </button>
                <button type="button" className={`rounded-full px-3 py-2 text-xs font-semibold ${language === "hi" ? "bg-primary text-white" : "bg-stone-100 text-stone-600"}`} onClick={() => onLanguageChange("hi")} data-testid="mobile-language-toggle-hi">
                  हिंदी
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </header>

      <main>{children}</main>

      <footer className="border-t border-stone-200 bg-stone-950 text-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.2fr_0.8fr_1fr] lg:px-8">
          <div className="space-y-5">
            <img src={contactDetails.logoPath} alt="Sharda Agrotech logo" className="h-20 w-20 rounded-3xl bg-white p-2" data-testid="footer-logo-image" />
            <p className="font-heading text-3xl font-semibold text-white" data-testid="footer-brand-name">
              {contactDetails.brandName}
            </p>
            <p className="max-w-lg text-sm leading-7 text-stone-300" data-testid="footer-brand-description">
              {content.footerText}
            </p>
            <div className="inline-flex rounded-full bg-primary/20 px-4 py-2 text-sm text-emerald-200" data-testid="footer-fssai-badge">
              FSSAI Licensed
            </div>
          </div>

          <div>
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.24em] text-stone-400" data-testid="footer-quick-links-title">
              {quickLinksLabel}
            </p>
            <div className="grid gap-3 text-sm text-stone-300">
              {navItems.map((item) => (
                <Link key={item.href} to={item.href} className="transition-colors hover:text-white" data-testid={`footer-nav-${item.href.replace(/\//g, "") || "home"}-link`}>
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.24em] text-stone-400" data-testid="footer-contact-title">
              {contactLabel}
            </p>
            <div className="grid gap-3 text-sm text-stone-300">
              <a href={contactDetails.phoneLink} className="flex items-start gap-3 transition-colors hover:text-white" data-testid="footer-phone-link">
                <PhoneCall className="mt-0.5 h-4 w-4 text-emerald-200" />
                <span>{contactDetails.phoneDisplay}</span>
              </a>
              <a href={contactDetails.whatsappLink} target="_blank" rel="noreferrer" className="flex items-start gap-3 transition-colors hover:text-white" data-testid="footer-whatsapp-link">
                <MessageSquareMore className="mt-0.5 h-4 w-4 text-emerald-200" />
                <span>{contactDetails.whatsappDisplay}</span>
              </a>
              <a href={contactDetails.emailLink} className="flex items-start gap-3 transition-colors hover:text-white" data-testid="footer-email-link">
                <Mail className="mt-0.5 h-4 w-4 text-emerald-200" />
                <span>{contactDetails.email}</span>
              </a>
              <div className="flex items-start gap-3" data-testid="footer-address-text">
                <MapPin className="mt-1 h-4 w-4 text-emerald-200" />
                <p className="leading-7 text-stone-300">{contactDetails.address}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 px-4 py-4 text-center text-xs text-stone-400" data-testid="footer-copyright-text">
          © {year} {contactDetails.brandName}. All rights reserved.
        </div>
      </footer>

      <a
        href={contactDetails.phoneLink}
        className="fixed bottom-5 right-5 inline-flex items-center gap-2 rounded-full bg-accent px-5 py-3 text-sm font-semibold text-white shadow-2xl shadow-orange-300/40 transition-all duration-300 ease-in-out hover:-translate-y-0.5"
        data-testid="floating-call-button"
      >
        <PhoneCall className="h-4 w-4" />
        {content.ctas.call}
      </a>
    </div>
  );
}